import React from "react";
import { TriangleAlert } from "lucide-react";
import AllocationEditor from "./AllocationEditor";
import { MAX_LEVERAGE } from "./stubData";
import { fmtUsd, fmtUsdSigned, clamp } from "./format";

/**
 * PairRow — one automatable strategy (venue pair) as a v3 console row.
 *
 * LAYOUT is the mockup's four-cell grid: pair + state pill · leverage line ·
 * money · allocation bar. Editable rows carry a second line under the grid for
 * the allocation and leverage controls, which the mockup (a static picture) has
 * no need for.
 *
 * THE COLOUR LAW: cyan for a strategy that is RUNNING, gold for anything gated
 * on a staked Supernova, amber for leverage risk stated inline on the row it
 * belongs to, red only for a slot whose stake has left.
 *
 * REAL data: `position` (open value / collateral) comes from
 * /status.openPositions; `automated` comes from /status.riskConfig.pairAllowlist;
 * `locked` comes from the engine's member-level slot pool (/status.staking,
 * ARC-53) — and a row also locks when that pool could NOT be read, which is why
 * `stakeKnown` exists: the caption for "you need one more stake" and the caption
 * for "we couldn't read your stake" must never be the same sentence, and the
 * second one may not contain a number.
 *
 * Allocation % and leverage are edited here and PERSISTED by WalletSection's
 * "Save allocations" (#9, allocation-only POST /config). Execution stays gated:
 * saved allocations do not size trades until the engine's audit-gated
 * ALLOCATION_SIZING_ENABLED flag is armed. `warning` carries WalletSection's
 * honesty notes (over-cap / below-gTrade-minimum) for this row.
 *
 * The engine's /status contract exposes sizeUsd/collateralUsd but no PnL. ARC-34
 * fills that from GET /report, which reports unrealized PnL per position and
 * marks an unpriced one with a null — so PnL is the engine's number when it has
 * one and an em dash when it does not, never a zero standing in for "unknown".
 * TODO(backend, supervised): per-position PnL on /status.openPositions too, so
 * the row does not need two endpoints to state one number.
 *
 * SUPERNOVA SLOT STATES (SUP-6) — all optional, all display-only:
 *   onUseOpenSlot  a locked row with a free slot behind it; takes the member to
 *                  the allowlist control that turns this pair on
 *   onStakeMore    a locked row with no free slot; links to the Supernova tab
 *   paused         an automated row whose slot lost the stake backing it
 * With none of them passed — which is every build where SUPERNOVA_TAB_ENABLED
 * is off — this row renders the same states it rendered before SUP-6.
 * The engine does not enforce the slot gate yet; no copy here may imply it does.
 */

/* "Stake 3 more Supernova" / "Stake 1 more Supernova" — the mockup's CTA copy,
   with the count the member actually needs for THIS row. */
const stakeMoreCopy = (needed) =>
  `Stake ${needed} more Supernova${needed === 1 ? "" : "s"} to allocate`;

/* The other reason a row can be locked (ARC-53). Deliberately carries no digit
   and makes no promise — it describes a read that didn't land. */
export const STAKE_UNKNOWN_COPY = "Locked — stake unavailable";

const PairRow = ({
  pair,
  automated,
  locked,
  allocationPct,
  leverage,
  onAllocationChange,
  onLeverageChange,
  position, // { pair, long, sizeUsd, collateralUsd } | undefined
  /* Unrealized PnL for this pair from GET /report (ARC-34). undefined ⇒ the
     engine didn't price it, and the cell says so rather than showing a zero. */
  pnlUsd,
  warning, // string | null — over-cap / below-min note from WalletSection
  /* SUPERBETA-6 — the engine's minimum viable allocation for THIS pair, from
     /status.sizingFloors. Absent ⇒ floors unknown ⇒ no mark, no warning. */
  floor = null, // { minPct, minCollateralUsd, minPositionUsd, leverage } | null
  belowFloor = false,
  /* Where this pair trades. Anything other than the default gTrade venue has no
     live data behind it yet and says so instead of showing blanks. */
  venue = "gtrade", // "gtrade" | "brokerage"
  paused = false, // slot unbacked (display state only — see the module comment)
  onUseOpenSlot, // () => void | undefined
  onStakeMore, // () => void | undefined
  stakeMoreNeeded = 1, // how many more stakes THIS row needs (stakeKnown only)
  /* ARC-53 — false when the row is locked because the slot pool could not be
     read, rather than because the member is short a stake. The row then states
     that plainly and shows NO count: a "stake 1 more" on an unread pool is a
     claim about a number nobody has. */
  stakeKnown = true,
  /* SUPERBETA-8 — the TradingView connect wizard's entry point for this row.
     `tvConnected` is the ENGINE's answer: it is true only where /status has a
     received signal on record for this pair, never a local guess. Undefined
     `onConnectTradingView` (every caller before SUPERBETA-8) renders no
     affordance at all and leaves this row exactly as it was. */
  tvConnected = false,
  onConnectTradingView, // () => void | undefined
}) => {
  const leveraged = !locked && Number(leverage) > 1;
  const pnlKnown = Number.isFinite(Number(pnlUsd));

  if (locked) {
    return (
      <div className="sa-row sa-rowlock" data-testid={`sa-row-${pair}`}>
        <div className="sa-pair">
          <span className="sa-nm sa-nm-dim">{pair}</span>
          <span className="sa-st sa-st-lock">Locked</span>
        </div>

        {/* The gate CTA lives in the leverage cell, exactly as the mockup puts
            it there: a locked row has no leverage to state. With the slot pool
            unread there is no CTA and no count — just what happened. */}
        <p className="sa-lev">
          {!stakeKnown ? (
            <span className="text-arc-ink-4">{STAKE_UNKNOWN_COPY}</span>
          ) : onUseOpenSlot ? (
            <button type="button" className="sa-locked-cta" onClick={onUseOpenSlot}>
              Use open slot →
            </button>
          ) : onStakeMore ? (
            <button type="button" className="sa-locked-cta" onClick={onStakeMore}>
              {stakeMoreCopy(stakeMoreNeeded)} →
            </button>
          ) : (
            stakeMoreCopy(stakeMoreNeeded)
          )}
        </p>

        <p className="sa-money sa-money-none">—</p>

        <div className="sa-alloc">
          <div className="sa-bar" aria-hidden="true">
            <div className="sa-fill sa-fill-gold" style={{ width: "0%" }} />
          </div>
          <span className="sa-pct">—</span>
        </div>
      </div>
    );
  }

  const pct = clamp(Number(allocationPct) || 0, 0, 100);

  return (
    <div className="sa-row" data-testid={`sa-row-${pair}`}>
      <div className="sa-pair">
        <span className="sa-nm">{pair}</span>
        {paused ? (
          /* Display state: this pair is still allocated, but the stake that
             backed its slot is gone. The engine enforces nothing here yet. */
          <span
            className="sa-st sa-st-paused"
            title="You have fewer Supernovas staked than strategies allocated. The engine doesn't enforce the slot gate yet."
          >
            Paused — slot unbacked
          </span>
        ) : automated ? (
          <span className="sa-st sa-st-auto">Automated</span>
        ) : (
          <span className="sa-st sa-st-idle">Not automated</span>
        )}
      </div>

      {/* Leverage, inline and amber when it is actually above 1x. */}
      <p className={["sa-lev", leveraged ? "sa-warn" : ""].join(" ").trim()}>
        {leveraged ? (
          <>
            <TriangleAlert size={11} aria-hidden="true" className="inline-block mr-1 -mt-0.5" />
            Leveraged {Number(leverage).toFixed(1)}x · leverage raises liquidation risk
          </>
        ) : (
          `No leverage · max ${MAX_LEVERAGE}x`
        )}
      </p>

      <p className={["sa-money", position ? "" : "sa-money-none"].join(" ").trim()}>
        {venue === "brokerage" && !position ? (
          /* Stock pairs route through a brokerage the engine isn't connected to
             yet, so there is no position or value to state. Say that, rather
             than render an em dash that reads as "flat". */
          <span className="text-caption">via brokerage — coming online in beta</span>
        ) : position ? (
          <>
            {fmtUsd(position.sizeUsd)}
            {pnlKnown ? (
              <span
                className={[
                  "sa-money-p",
                  pnlUsd > 0 ? "sa-up" : pnlUsd < 0 ? "sa-down" : "text-arc-ink-4",
                ].join(" ")}
                title="Unrealized PnL on this position, reported by the engine. A past result, not a forecast."
              >
                · PnL {fmtUsdSigned(pnlUsd)}
              </span>
            ) : (
              <span
                className="sa-money-p text-arc-ink-4"
                title="The engine hasn't priced this position, so there is no PnL to show."
              >
                · PnL —
              </span>
            )}
          </>
        ) : (
          "No open position"
        )}
      </p>

      <div className="sa-alloc">
        <div className="sa-bar" aria-hidden="true">
          <div className="sa-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="sa-pct">{pct}%</span>
      </div>

      {/* ── the controls the mockup has no slot for ─────────────────────── */}
      <div className="sa-row-controls">
        <AllocationEditor
          pair={pair}
          value={allocationPct}
          onChange={onAllocationChange}
          floor={floor}
          below={belowFloor}
        />

        <span className="text-caption text-arc-ink-3 ml-2">Leverage</span>
        <input
          type="number"
          value={leverage}
          min="1"
          max={MAX_LEVERAGE}
          step="0.1"
          onChange={(e) => onLeverageChange(clamp(Number(e.target.value) || 1, 1, MAX_LEVERAGE))}
          className="w-[58px] px-2 py-1 text-sm text-right tnum"
          aria-label={`${pair} leverage`}
        />
        <span className="text-sm text-arc-ink-3">x</span>

        {/* SUPERBETA-8: the one-minute route from this row to a connected
            TradingView alert. Connected rows state it and offer the manage
            screen instead — the same wizard, opened on its last step. */}
        {onConnectTradingView && (
          <span className="sa-tv-cell">
            {tvConnected ? (
              <>
                <span className="sa-st sa-st-auto" data-testid={`sa-tv-on-${pair}`}>
                  TradingView connected
                </span>
                <button
                  type="button"
                  className="sa-tv-link"
                  onClick={onConnectTradingView}
                  data-testid={`sa-tv-manage-${pair}`}
                >
                  Manage
                </button>
              </>
            ) : (
              <button
                type="button"
                className="sa-tv-link"
                onClick={onConnectTradingView}
                data-testid={`sa-tv-connect-${pair}`}
              >
                Connect TradingView →
              </button>
            )}
          </span>
        )}

        {/* honesty warning (#9): over-cap / below-gTrade-minimum, from WalletSection */}
        {warning && (
          <span
            role="status"
            className="text-caption text-arc-warn inline-flex items-start gap-1 basis-full"
          >
            <TriangleAlert size={12} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>{warning}</span>
          </span>
        )}
      </div>
    </div>
  );
};

export default PairRow;
